import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001/api/v1';

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  registerPremium: (data) => api.post('/auth/register-premium', data),
  registerGestor: (data) => api.post('/auth/register-gestor', data),
  setPassword: (data) => api.post('/auth/set-password', data),
};

export const usersAPI = {
  getMe: () => api.get('/users/me'),
  updateMe: (data) => api.put('/users/me', data),
  getAll: (params) => api.get('/users/', { params }),
};

export const storeAPI = {
  getCategories: () => api.get('/store/categories'),
  getCategoryTree: () => api.get('/store/category-tree'),
  getProducts: (params) => api.get('/store/products', { params }),
  getFeaturedProducts: (limit) => api.get('/store/products/featured', { params: { limit } }),
  getProduct: (id) => api.get(`/store/products/${id}`),
  getCategoryProducts: (slug, params) => api.get(`/store/categories/${slug}/products`, { params }),
  getSubcategoryProducts: (id) => api.get(`/store/subcategories/${id}/products`),
  getFamilyProducts: (id) => api.get(`/store/families/${id}/products`),
  getPacks: () => api.get('/store/packs'),
  getPack: (id) => api.get(`/store/packs/${id}`),
  search: (q, params) => api.get('/store/search', { params: { q, ...params } }),
};

export const cartAPI = {
  getCart: () => api.get('/cart/'),
  addItem: (data) => api.post('/cart/items', data),
  updateItem: (id, data) => api.put(`/cart/items/${id}`, data),
  removeItem: (id) => api.delete(`/cart/items/${id}`),
};

export const ordersAPI = {
  create: (data) => api.post('/orders/', data),
  getMyOrders: (params) => api.get('/orders/', { params }),
  getOrder: (id) => api.get(`/orders/${id}`),
};

export const paymentsAPI = {
  getMethods: () => api.get('/payments/methods'),
  createStripeIntent: (data) => api.post('/payments/stripe/create-intent', data),
  confirmStripePayment: (data) => api.post('/payments/stripe/confirm', data),
  payWithWallet: (data) => api.post('/payments/wallet/pay', data),
};

export const adminAPI = {
  getStats: () => api.get('/admin/stats'),
  getUsers: (params) => api.get('/admin/users', { params }),
  getUser: (id) => api.get(`/admin/users/${id}`),
  updateUser: (id, data) => api.put(`/admin/users/${id}`, data),
  getCategories: () => api.get('/admin/categories'),
  createCategory: (data) => api.post('/admin/categories', data),
  updateCategory: (id, data) => api.put(`/admin/categories/${id}`, data),
  deleteCategory: (id) => api.delete(`/admin/categories/${id}`),
  getSubcategories: (params) => api.get('/admin/subcategories', { params }),
  createSubcategory: (data) => api.post('/admin/subcategories', data),
  updateSubcategory: (id, data) => api.put(`/admin/subcategories/${id}`, data),
  deleteSubcategory: (id) => api.delete(`/admin/subcategories/${id}`),
  getFamilies: (params) => api.get('/admin/families', { params }),
  createFamily: (data) => api.post('/admin/families', data),
  updateFamily: (id, data) => api.put(`/admin/families/${id}`, data),
  deleteFamily: (id) => api.delete(`/admin/families/${id}`),
  getPacks: (params) => api.get('/admin/packs', { params }),
  createPack: (data) => api.post('/admin/packs', data),
  updatePack: (id, data) => api.put(`/admin/packs/${id}`, data),
  deletePack: (id) => api.delete(`/admin/packs/${id}`),
  getPack: (id) => api.get(`/admin/packs/${id}`),
  getProducts: (params) => api.get('/admin/products', { params }),
  createProduct: (data) => api.post('/admin/products', data),
  updateProduct: (id, data) => api.put(`/admin/products/${id}`, data),
  deleteProduct: (id) => api.delete(`/admin/products/${id}`),
  getOrders: (params) => api.get('/admin/orders', { params }),
  getOrder: (id) => api.get(`/admin/orders/${id}`),
  updateOrderStatus: (id, data) => api.put(`/admin/orders/${id}/status`, data),
  getSettings: () => api.get('/admin/settings'),
  updateSettings: (data) => api.put('/admin/settings', data),
};

export const mlmAPI = {
  getNetworkStats: () => api.get('/mlm/network-stats'),
  getActiveStatus: () => api.get('/mlm/active-status'),
  getWallet: () => api.get('/mlm/wallet'),
  getBinaryTree: () => api.get('/mlm/binary-tree'),
  getUnilevelTree: (levels = 5) => api.get(`/mlm/unilevel-tree?levels=${levels}`),
  getRankHistory: () => api.get('/mlm/rank-history'),
  getCommissionHistory: (params) => api.get('/mlm/commission-history', { params }),
  getWalletTransactions: (params) => api.get('/mlm/wallet/transactions', { params }),
  getCycles: (params) => api.get('/mlm/cycles', { params }),
  getSttoraPoints: () => api.get('/mlm/sttora-points'),
  getSttoraPointsTransactions: (params) => api.get('/mlm/sttora-points/transactions', { params }),
  redeemSttoraPoints: (data) => api.post('/mlm/sttora-points/redeem', data),
  getPoolQualifications: () => api.get('/mlm/pool-qualifications'),
  requestWithdrawal: (amount) => api.post('/mlm/wallet/withdraw', { amount }),
};

export const adminMlmAPI = {
  getGestores: (params) => api.get('/admin/mlm/gestores', { params }),
  getRankConfig: () => api.get('/admin/mlm/rank-config'),
  updateRankConfig: (id, data) => api.put(`/admin/mlm/rank-config/${id}`, data),
  getCycles: (params) => api.get('/admin/mlm/cycles', { params }),
  createCycle: () => api.post('/admin/mlm/cycles'),
  processCycle: (id) => api.post(`/admin/mlm/cycles/${id}/process`),
  getActiveStatus: () => api.get('/admin/mlm/active-status'),
  resetActiveStatus: () => api.post('/admin/mlm/reset-active-status'),
  getPoolRuns: () => api.get('/admin/mlm/global-pool/runs'),
  initializePools: () => api.post('/admin/mlm/global-pool/initialize'),
  runPool: (data) => api.post('/admin/mlm/global-pool/run', data),
  runDegradationCheck: () => api.post('/admin/mlm/degradation/check'),
  getUserNetwork: (userId) => api.get(`/admin/mlm/network/${userId}`),
  getNetworkOverview: () => api.get('/admin/mlm/network-overview'),
  repositionNode: (positionId, data) => api.put(`/admin/mlm/network/${positionId}/reposition`, data),
  getAvailablePositions: (bcId, maxLevel) => api.get(`/admin/mlm/network/${bcId}/available-positions`, { params: { max_level: maxLevel || 5 } }),
  getBestPosition: (bcId, sponsorUserId) => api.get(`/admin/mlm/network/${bcId}/best-position/${sponsorUserId}`),
};

export const payoutsAPI = {
  getRequests: (params) => api.get('/payouts/requests', { params }),
  createRequest: (data) => api.post('/payouts/requests', data),
  approveRequest: (id, data) => api.put(`/payouts/requests/${id}/approve`, data),
  rejectRequest: (id, data) => api.put(`/payouts/requests/${id}/reject`, data),
  getHistory: (params) => api.get('/payouts/history', { params }),
  getSummary: () => api.get('/payouts/summary'),
};

export const commissionRulesAPI = {
  getAll: () => api.get('/admin/mlm/commission-rules'),
  update: (id, data) => api.put(`/admin/mlm/commission-rules/${id}`, data),
};

export const reportsAPI = {
  getOverview: () => api.get('/admin/reports/overview'),
  getSalesByType: () => api.get('/admin/reports/sales-by-type'),
  getTopGestores: () => api.get('/admin/reports/top-gestores'),
  getRankDistribution: () => api.get('/admin/reports/rank-distribution'),
  getAllCommissions: () => api.get('/admin/mlm/all-commission-history'),
};

export function getApiError(err, fallback = 'Error inesperado') {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map(e => e.msg).join('; ') || fallback;
  }
  return fallback;
}

export default api;
